import type { TFunction } from "i18next";

import {
  VENDOR_HANDOFF_CHECK_FIELD_LABELS,
  type VendorHandoffCheck,
} from "@nexus/contracts";

const FIELD_I18N: Record<string, { key: string; fallback: string }> = {
  orderInstructions: {
    key: "marketplace.sendHandoff.orderNoteTitle",
    fallback: "Order-specific instructions",
  },
  productAccuracy: {
    key: "marketplace.handoff.fields.productAccuracy.label",
    fallback: "Product accuracy",
  },
  brandGuidelines: {
    key: "marketplace.handoff.fields.brandGuidelines.label",
    fallback: "Brand guidelines",
  },
  techSpecs: {
    key: "marketplace.handoff.fields.techSpecs.label",
    fallback: "Tech specs",
  },
  policyRestrictions: {
    key: "marketplace.handoff.fields.policyRestrictions.label",
    fallback: "Policy & restrictions",
  },
  dosAndDonts: {
    key: "marketplace.handoff.fields.dosAndDonts.label",
    fallback: "Do's & Don'ts",
  },
  fontsLicenceProof: {
    key: "marketplace.handoff.fields.fontsLicenceProof.label",
    fallback: "Fonts + licence proof",
  },
  coloursHex: {
    key: "marketplace.handoff.fields.coloursHex.label",
    fallback: "Colours / hex",
  },
  logos: {
    key: "marketplace.handoff.fields.logos.label",
    fallback: "Logos",
  },
  thumbnailGuidelines: {
    key: "marketplace.handoff.fields.thumbnailGuidelines.label",
    fallback: "Thumbnail guidelines",
  },
};

const CAMEL_CASE_FIELD = /^([a-z][a-zA-Z0-9]+)\b/;

export function titleFromCamelCase(value: string): string {
  const spaced = value
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!spaced) return value;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function translateHandoffFieldLabel(field: string, t: TFunction): string {
  const entry = FIELD_I18N[field];
  if (entry) {
    const translated = t(entry.key, { defaultValue: entry.fallback });
    if (translated && translated !== entry.key) return translated;
    return entry.fallback;
  }
  return VENDOR_HANDOFF_CHECK_FIELD_LABELS[field] ?? titleFromCamelCase(field);
}

function fieldKeyFromCheck(check: VendorHandoffCheck): string | undefined {
  if (check.field) return check.field;
  const match = CAMEL_CASE_FIELD.exec(check.message);
  return match?.[1];
}

function interpolate(t: TFunction, key: string, defaultValue: string, vars: Record<string, string>): string {
  return t(key, { defaultValue, ...vars });
}

export function formatVendorHandoffCheck(check: VendorHandoffCheck, t: TFunction): string {
  const fieldKey = fieldKeyFromCheck(check);
  const label = fieldKey ? translateHandoffFieldLabel(fieldKey, t) : null;
  if (check.code === "placeholder_text" && label) {
    return interpolate(
      t,
      "marketplace.sendHandoff.checkPlaceholder",
      "{{field}} looks like placeholder text.",
      { field: label },
    );
  }
  if (check.code === "internal_link" && label) {
    return interpolate(
      t,
      "marketplace.sendHandoff.checkInternalLink",
      "{{field}} includes a link production partners cannot open.",
      { field: label },
    );
  }
  if (label && fieldKey && check.message.startsWith(fieldKey)) {
    return `${label}${check.message.slice(fieldKey.length)}`;
  }
  return check.message;
}

export interface GroupedVendorHandoffChecks {
  placeholders: string[];
  internalLinks: string[];
  other: Array<{ code: string; message: string }>;
}

export function groupVendorHandoffChecks(
  checks: readonly VendorHandoffCheck[],
  t: TFunction,
): GroupedVendorHandoffChecks {
  const placeholders: string[] = [];
  const internalLinks: string[] = [];
  const other: Array<{ code: string; message: string }> = [];

  for (const check of checks) {
    const fieldKey = fieldKeyFromCheck(check);
    if (check.code === "placeholder_text" && fieldKey) {
      placeholders.push(translateHandoffFieldLabel(fieldKey, t));
      continue;
    }
    if (check.code === "internal_link" && fieldKey) {
      internalLinks.push(translateHandoffFieldLabel(fieldKey, t));
      continue;
    }
    other.push({ code: check.code, message: formatVendorHandoffCheck(check, t) });
  }

  return { placeholders, internalLinks, other };
}
